import { useCallback, type Dispatch, type SetStateAction } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { useTranslation } from '@mint-lib/i18n';
import { useIsAgencyCompany, useProfile } from '@mint-lib/profile-helpers';
import { typographyVariants } from '@mint-lib/tw-components';

import ConditionalSkeleton from '../../atoms/ConditionalSkeleton/ConditionalSkeleton.jsx';
import FormBlock from '../../atoms/FormBlock/index.js';
import InputField from '../../atoms/InputField/index.js';
import SelectField from '../../molecules/SelectField/SelectField.jsx';
import type { OptionType } from '../../types/OptionType.js';
import type { UserModalViewType } from './userModal.types.js';
import { UserModalType } from './validation.js';

export type UserView = {
  formInstance: UseFormReturn<UserModalType>;
  isReadonlyPermission: boolean;
  roleOptions: OptionType[];
  clientsOptions: OptionType[] | null;
  assignedClients: number[] | null;
  setAssignedClients: Dispatch<SetStateAction<number[] | null>>;
  isLoading: boolean;
  isSaving: boolean;
  view: UserModalViewType;
};

const UserView = ({
  formInstance,
  isReadonlyPermission,
  roleOptions,
  clientsOptions,
  assignedClients,
  setAssignedClients,
  isLoading,
  isSaving,
  view,
}: UserView) => {
  const { t } = useTranslation('@myn/permissions');

  const { profile } = useProfile();

  const isAgencyCompany = useIsAgencyCompany();

  const isOwnUser = formInstance.getValues('id') === profile?.id;

  const handleRoleChange = useCallback(
    (value: OptionType['value']) => {
      formInstance.setValue('role', Number(value), {
        shouldDirty: true,
      });
      formInstance.trigger('role');
    },
    [formInstance],
  );

  const handleAssignedClientsChange = useCallback(
    (selected: OptionType['value'][]) => {
      const e = selected.map(Number);
      if (e) {
        setAssignedClients(e);

        formInstance.setValue('assignedClients', e, {
          shouldDirty: true,
        });

        formInstance.trigger('assignedClients');
      }
    },
    [formInstance, setAssignedClients],
  );

  return (
    <>
      <div className="flex flex-col gap-2">
        <h6 className={typographyVariants({ variant: 'h6' })}>
          {t('General information')}
        </h6>
        <ConditionalSkeleton height={60} width={488} isLoading={isLoading}>
          <InputField
            label={t('Email*')}
            placeholder={t('Enter email')}
            error={!!formInstance.formState.errors.email?.message}
            helperText={formInstance.formState.errors.email?.message}
            onChange={(e) => {
              formInstance.setValue('email', e.target.value);
            }}
            name="email"
            disabled
          />
        </ConditionalSkeleton>
      </div>

      <FormBlock>
        <ConditionalSkeleton height={60} width={240} isLoading={isLoading}>
          <InputField
            label={t('First name*')}
            placeholder={t('Enter first name')}
            name="firstName"
            data-dd-action-name="User modal: first name"
            disabled={
              view === 'existingUserView' || isReadonlyPermission || isSaving
            }
          />
        </ConditionalSkeleton>
        <ConditionalSkeleton height={60} width={240} isLoading={isLoading}>
          <InputField
            label={t('Last name*')}
            placeholder={t('Enter last name')}
            name="lastName"
            data-dd-action-name="User modal: last name"
            disabled={
              view === 'existingUserView' || isReadonlyPermission || isSaving
            }
          />
        </ConditionalSkeleton>
      </FormBlock>

      <FormBlock>
        <div className="grow">
          <ConditionalSkeleton height={60} width={488} isLoading={isLoading}>
            <SelectField
              id="role"
              label={t('Role*')}
              placeholder={t('Select role')}
              options={roleOptions}
              value={Number(formInstance.getValues('role')) || ''}
              disabled={isReadonlyPermission || isOwnUser || isSaving}
              infoText={t(`This role will be applied to the agency workspace.`)}
              error={Boolean(formInstance.formState.errors.role?.message)}
              helperText={formInstance.formState.errors.role?.message}
              onChange={handleRoleChange}
              data-dd-action-name="User modal: role"
            />
          </ConditionalSkeleton>
        </div>
      </FormBlock>

      {isAgencyCompany ? (
        <FormBlock>
          <div className="max-w-122 grow">
            <ConditionalSkeleton height={60} width={488} isLoading={isLoading}>
              <SelectField
                multiple
                id="assignedClients"
                label={t('Clients')}
                placeholder={t('Assign clients')}
                infoText={t(`Assign clients to this user`)}
                options={clientsOptions ?? []}
                value={assignedClients ?? []}
                disabled={isReadonlyPermission || isSaving}
                onChange={handleAssignedClientsChange}
                data-dd-action-name="User modal: clients"
              />
            </ConditionalSkeleton>
          </div>
        </FormBlock>
      ) : null}
    </>
  );
};

export default UserView;
